import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  BulkMergeListingsInput,
  Event,
  ListingClient,
} from 'src/WebApiController';

import { UseGetListingMergeSuggestions } from './UseGetListingMergeSuggestions.types';
import { clusterListingMergeSuggestionsByListingIdOverlap } from './useGetListingMergeSuggestions.utils';

export const useGetListingMergeSuggestionsByEvent = (
  event?: Event | null,
  excludeHeldListings = false
): UseGetListingMergeSuggestions => {
  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null &&
    event?.viagId != null;

  const mergeSuggestionsQuery = useQuery({
    queryKey: [
      'ListingClient.getListingMergeSuggestionsByEvent',
      activeAccountWebClientConfig,
      event?.viagId,
      excludeHeldListings,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return new ListingClient(
        activeAccountWebClientConfig
      ).getListingMergeSuggestionsByEvent(event!.viagId!, excludeHeldListings);
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    meta: {
      persist: false,
      onError: (error: ErrorTypes) => {
        trackError('ListingClient.getListingMergeSuggestionsByEvent', error, {
          trackErrorData: {
            viagogoEventId: event?.viagId,
            excludeHeldListings,
          },
        });
      },
    },
  });

  const mergeSuggestions = useMemo(() => {
    return clusterListingMergeSuggestionsByListingIdOverlap(
      mergeSuggestionsQuery.data
    );
  }, [mergeSuggestionsQuery.data]);

  const firstListingIdToAccountIdMap = useMemo(() => {
    const map: Record<number, string | null> = {};
    mergeSuggestions?.forEach((s) => {
      if (s.listingIds.length) {
        map[s.listingIds[0]] = s.accId;
      }
    });
    return map;
  }, [mergeSuggestions]);

  const mapListingIdsListWithOwnership = useCallback(
    (input: BulkMergeListingsInput) => {
      if (
        input.listingIdLists.every(
          (ids) => firstListingIdToAccountIdMap[ids[0]] != null
        )
      ) {
        const consigneeOwnership: Record<number, string> = {};
        input.listingIdLists.forEach((ids) => {
          consigneeOwnership[ids[0]] = firstListingIdToAccountIdMap[ids[0]]!;
        });
        return {
          ...input,
          consigneeOwnership,
        };
      }

      return input;
    },
    [firstListingIdToAccountIdMap]
  );

  return {
    mergeSuggestions,
    mapListingIdsListWithOwnership,
    isLoading: mergeSuggestionsQuery.isLoading,
  };
};
